/**
 * Entity store — SQLite-backed implementation.
 * CRUD operations for entities, relationships, and timeline events.
 * Uses $1, $2 numbered placeholders for @tauri-apps/plugin-sql compatibility.
 *
 * The `attributes` DB column stores a JSON object with three sub-keys:
 *   { properties, provenance, corroboration }
 * Legacy entities (Phase 1) without this structure are handled gracefully.
 */

import type {
  Entity,
  EntityStore,
  EntityQuery,
  Provenance,
  Corroboration,
  Relationship,
  TimelineEvent,
} from './types';
import { getDatabase } from '@/storage/database';
import { eventBus } from '@/core/events';

function generateId(): string {
  return crypto.randomUUID();
}

function now(): string {
  return new Date().toISOString();
}

/** Default provenance for entities created without explicit provenance. */
const DEFAULT_PROVENANCE: Provenance = {
  sourceUrl: '',
  extractionQuote: '',
  collectorId: 'manual',
  collectedAt: '',
  confidence: 0,
};

/** Default corroboration for uncorroborated entities. */
const DEFAULT_CORROBORATION: Corroboration = {
  count: 0,
  sources: [],
  natoRating: 'F6',
};

/** Row shape as stored in SQLite entities table. */
interface EntityRow {
  id: string;
  type: string;
  name: string;
  aliases: string | null;
  description: string | null;
  attributes: string | null; // JSON: { properties, provenance, corroboration }
  first_seen: string;
  last_seen: string;
  confidence: number | null;
  tags: string | null;
  notes: string | null;
  watch_status: string;
  created_at: string;
  updated_at: string;
}

/** Row shape for relationships table. */
interface RelationshipRow {
  id: string;
  source_entity_id: string;
  target_entity_id: string;
  type: string;
  confidence: number | null;
  evidence: string | null;
  first_seen: string;
  last_seen: string;
}

function rowToEntity(row: EntityRow): Entity {
  let attrs: Record<string, unknown> = {};
  if (row.attributes) {
    try { attrs = JSON.parse(row.attributes); } catch { attrs = {}; }
  }

  // Handle both new format (nested properties/provenance/corroboration)
  // and legacy format (flat attributes object from Phase 1)
  const hasNewFormat = 'provenance' in attrs || 'corroboration' in attrs || 'properties' in attrs;
  const properties = hasNewFormat
    ? ((attrs.properties as Record<string, unknown>) ?? {})
    : attrs;
  const provenance = (attrs.provenance as Provenance | undefined) ?? DEFAULT_PROVENANCE;
  const corroboration = (attrs.corroboration as Corroboration | undefined) ?? DEFAULT_CORROBORATION;

  return {
    id: row.id,
    type: row.type as Entity['type'],
    name: row.name,
    aliases: row.aliases ? JSON.parse(row.aliases) : [],
    description: row.description ?? undefined,
    properties,
    provenance,
    corroboration,
    sources: [],
    relationships: [],
    firstSeen: row.first_seen,
    lastSeen: row.last_seen,
    confidence: row.confidence ?? 0,
    tags: row.tags ? JSON.parse(row.tags) : [],
    notes: row.notes ? JSON.parse(row.notes) : [],
    watchStatus: (row.watch_status as Entity['watchStatus']) ?? 'none',
  };
}

function entityToAttributesJSON(entity: {
  properties?: Record<string, unknown>;
  provenance?: Provenance;
  corroboration?: Corroboration;
}): string {
  return JSON.stringify({
    properties: entity.properties ?? {},
    provenance: entity.provenance ?? DEFAULT_PROVENANCE,
    corroboration: entity.corroboration ?? DEFAULT_CORROBORATION,
  });
}

function rowToRelationship(row: RelationshipRow): Relationship {
  return {
    id: row.id,
    sourceEntityId: row.source_entity_id,
    targetEntityId: row.target_entity_id,
    type: row.type as Relationship['type'],
    confidence: row.confidence ?? 0,
    evidence: row.evidence ? JSON.parse(row.evidence) : [],
    firstSeen: row.first_seen,
    lastSeen: row.last_seen,
  };
}

class SQLiteEntityStore implements EntityStore {
  private getDb() {
    const db = getDatabase();
    if (!db) throw new Error('Database not initialized — call initDatabase() first');
    return db;
  }

  async create(entity: Omit<Entity, 'id'>): Promise<Entity> {
    const db = this.getDb();
    const id = generateId();
    const timestamp = now();

    await db.execute(
      `INSERT INTO entities (id, type, name, aliases, description, attributes, first_seen, last_seen, confidence, tags, notes, watch_status, created_at, updated_at)
       VALUES ($1, $2, $3, $4, $5, $6, $7, $8, $9, $10, $11, $12, $13, $14)`,
      [
        id,
        entity.type,
        entity.name,
        JSON.stringify(entity.aliases ?? []),
        entity.description ?? null,
        entityToAttributesJSON(entity),
        entity.firstSeen ?? timestamp,
        entity.lastSeen ?? timestamp,
        entity.confidence ?? 0,
        JSON.stringify(entity.tags ?? []),
        JSON.stringify(entity.notes ?? []),
        entity.watchStatus ?? 'none',
        timestamp,
        timestamp,
      ],
    );

    const created: Entity = {
      ...entity,
      id,
      aliases: entity.aliases ?? [],
      properties: entity.properties ?? {},
      provenance: entity.provenance ?? DEFAULT_PROVENANCE,
      corroboration: entity.corroboration ?? DEFAULT_CORROBORATION,
      sources: entity.sources ?? [],
      relationships: entity.relationships ?? [],
      firstSeen: entity.firstSeen ?? timestamp,
      lastSeen: entity.lastSeen ?? timestamp,
      confidence: entity.confidence ?? 0,
      tags: entity.tags ?? [],
      notes: entity.notes ?? [],
      watchStatus: entity.watchStatus ?? 'none',
    };

    eventBus.emit('entity:created', created);
    return created;
  }

  async getById(id: string): Promise<Entity | null> {
    const db = this.getDb();
    const rows = await db.query<EntityRow>(
      'SELECT * FROM entities WHERE id = $1',
      [id],
    );
    if (rows.length === 0) return null;
    return rowToEntity(rows[0]);
  }

  async search(query: EntityQuery): Promise<Entity[]> {
    const db = this.getDb();
    const conditions: string[] = [];
    const params: unknown[] = [];
    let paramIdx = 1;

    if (query.type) {
      conditions.push(`type = $${paramIdx++}`);
      params.push(query.type);
    }

    if (query.watchStatus) {
      conditions.push(`watch_status = $${paramIdx++}`);
      params.push(query.watchStatus);
    }

    if (query.text) {
      conditions.push(`(name LIKE $${paramIdx} OR description LIKE $${paramIdx})`);
      paramIdx++;
      params.push(`%${query.text}%`);
    }

    if (query.tags && query.tags.length > 0) {
      // Search for any matching tag in the JSON array
      const tagConditions = query.tags.map(() => {
        return `tags LIKE $${paramIdx++}`;
      });
      conditions.push(`(${tagConditions.join(' OR ')})`);
      for (const tag of query.tags) {
        params.push(`%"${tag}"%`);
      }
    }

    let sql = 'SELECT * FROM entities';
    if (conditions.length > 0) {
      sql += ' WHERE ' + conditions.join(' AND ');
    }
    sql += ' ORDER BY last_seen DESC';

    if (query.limit) {
      sql += ` LIMIT $${paramIdx++}`;
      params.push(query.limit);
    }
    if (query.offset) {
      sql += ` OFFSET $${paramIdx++}`;
      params.push(query.offset);
    }

    const rows = await db.query<EntityRow>(sql, params);
    return rows.map(rowToEntity);
  }

  async update(id: string, changes: Partial<Entity>): Promise<Entity> {
    const db = this.getDb();
    const existing = await this.getById(id);
    if (!existing) throw new Error(`Entity ${id} not found`);

    const updated = { ...existing, ...changes };
    const timestamp = now();

    await db.execute(
      `UPDATE entities SET
        type = $1, name = $2, aliases = $3, description = $4,
        attributes = $5, last_seen = $6, confidence = $7,
        tags = $8, notes = $9, watch_status = $10, updated_at = $11
       WHERE id = $12`,
      [
        updated.type,
        updated.name,
        JSON.stringify(updated.aliases),
        updated.description ?? null,
        entityToAttributesJSON(updated),
        updated.lastSeen ?? timestamp,
        updated.confidence,
        JSON.stringify(updated.tags),
        JSON.stringify(updated.notes),
        updated.watchStatus,
        timestamp,
        id,
      ],
    );

    eventBus.emit('entity:updated', updated);
    return updated;
  }

  async delete(id: string): Promise<void> {
    const db = this.getDb();

    // Cascade: delete all referencing records before removing the entity
    await db.execute(
      'DELETE FROM relationships WHERE source_entity_id = $1 OR target_entity_id = $1',
      [id],
    );
    await db.execute(
      'DELETE FROM entity_notes WHERE entity_id = $1',
      [id],
    );
    await db.execute(
      'DELETE FROM investigation_entities WHERE entity_id = $1',
      [id],
    );

    await db.execute('DELETE FROM entities WHERE id = $1', [id]);
    eventBus.emit('entity:deleted', id);
  }

  async addRelationship(rel: Omit<Relationship, 'id'>): Promise<Relationship> {
    const db = this.getDb();
    const id = generateId();
    const timestamp = now();

    await db.execute(
      `INSERT INTO relationships (id, source_entity_id, target_entity_id, type, confidence, evidence, first_seen, last_seen)
       VALUES ($1, $2, $3, $4, $5, $6, $7, $8)`,
      [
        id,
        rel.sourceEntityId,
        rel.targetEntityId,
        rel.type,
        rel.confidence ?? 0,
        JSON.stringify(rel.evidence ?? []),
        rel.firstSeen ?? timestamp,
        rel.lastSeen ?? timestamp,
      ],
    );

    const created: Relationship = { ...rel, id };
    return created;
  }

  async getRelationships(entityId: string): Promise<Relationship[]> {
    const db = this.getDb();
    const rows = await db.query<RelationshipRow>(
      'SELECT * FROM relationships WHERE source_entity_id = $1 OR target_entity_id = $1',
      [entityId],
    );
    return rows.map(rowToRelationship);
  }

  async getTimeline(entityId: string): Promise<TimelineEvent[]> {
    this.getDb(); // Ensure DB is initialized
    const timeline: TimelineEvent[] = [];

    // Get relationships involving this entity (as timeline of connections)
    const rels = await this.getRelationships(entityId);
    for (const rel of rels) {
      timeline.push({
        timestamp: rel.firstSeen,
        type: 'relationship',
        description: `${rel.type} relationship (confidence: ${(rel.confidence * 100).toFixed(0)}%)`,
        entityId,
      });
    }

    // Sort chronologically
    timeline.sort((a, b) => a.timestamp.localeCompare(b.timestamp));
    return timeline;
  }
}

export const entityStore: EntityStore = new SQLiteEntityStore();

// ---------------------------------------------------------------------------
// Entity notes / annotations — WP-S15.4
// Uses the entity_notes table (migration 002).
// ---------------------------------------------------------------------------

export interface EntityNote {
  id: string;
  entityId: string;
  text: string;
  createdAt: string;
}

interface EntityNoteRow {
  id: string;
  entity_id: string;
  text: string;
  created_at: string;
}

function rowToEntityNote(row: EntityNoteRow): EntityNote {
  return {
    id: row.id,
    entityId: row.entity_id,
    text: row.text,
    createdAt: row.created_at,
  };
}

/**
 * Add a note to an entity. Persists in the entity_notes table.
 */
export async function addEntityNote(entityId: string, text: string): Promise<EntityNote> {
  const db = getDatabase();
  if (!db) throw new Error('Database not initialized');

  const id = `note_${generateId()}`;
  const ts = now();

  await db.execute(
    'INSERT INTO entity_notes (id, entity_id, text, created_at) VALUES ($1, $2, $3, $4)',
    [id, entityId, text, ts],
  );

  return { id, entityId, text, createdAt: ts };
}

/**
 * Get all notes for an entity, ordered by creation time (newest first).
 */
export async function getEntityNotes(entityId: string): Promise<EntityNote[]> {
  const db = getDatabase();
  if (!db) throw new Error('Database not initialized');

  const rows = await db.query<EntityNoteRow>(
    'SELECT * FROM entity_notes WHERE entity_id = $1 ORDER BY created_at DESC',
    [entityId],
  );
  return rows.map(rowToEntityNote);
}

/**
 * Delete a specific entity note.
 */
export async function deleteEntityNote(noteId: string): Promise<void> {
  const db = getDatabase();
  if (!db) throw new Error('Database not initialized');

  await db.execute('DELETE FROM entity_notes WHERE id = $1', [noteId]);
}

/**
 * Search notes across all entities. Returns notes matching the query text.
 */
export async function searchEntityNotes(query: string): Promise<EntityNote[]> {
  const db = getDatabase();
  if (!db) throw new Error('Database not initialized');

  const rows = await db.query<EntityNoteRow>(
    'SELECT * FROM entity_notes WHERE text LIKE $1 ORDER BY created_at DESC',
    [`%${query}%`],
  );
  return rows.map(rowToEntityNote);
}
